import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSettings } from '@/features/config/hooks/use-config';
import { errorMessageKey } from '@/shared/lib/errors';
import { normalizeSaudiPhone } from '@/shared/lib/phone';
import { ArchHeader } from '@/shared/ui/ArchHeader';
import { Button } from '@/shared/ui/Button';
import { TextField } from '@/shared/ui/TextField';
import { useUiStore } from '@/stores/ui.store';

import { useEmailAuth, useSendOtp, useVerifyOtp } from '../hooks/use-auth';

/** Phone-OTP sign in. On verify success the AuthProvider updates the session and
 * the RootGate routes onward. Which step renders is runtime-configurable via
 * `app_settings` (auth.email_login / auth.phone_otp, Phase 10) — both default
 * to their REGISTRY default while the config query is still loading. */
export function LoginRoute(): JSX.Element {
  const { t } = useTranslation();
  const toggleLocale = useUiStore((s) => s.toggleLocale);
  const [phone, setPhone] = useState<string | null>(null);
  const { flag } = useAppSettings();
  const emailLoginEnabled = flag('auth.email_login');
  const phoneOtpEnabled = flag('auth.phone_otp');

  return (
    <>
      <ArchHeader mark="ب" />
      <main className="page stack">
        <div style={{ textAlign: 'center' }}>
          <h1>{t('app.title')}</h1>
          <p className="muted">{t('app.tagline')}</p>
        </div>
        {emailLoginEnabled ? (
          <EmailStep />
        ) : phoneOtpEnabled ? (
          phone === null ? (
            <PhoneStep onSent={setPhone} />
          ) : (
            <OtpStep phone={phone} onBack={() => setPhone(null)} />
          )
        ) : (
          <p className="muted" role="alert">
            {t('auth.noMethodAvailable')}
          </p>
        )}
        <Button variant="pill" onClick={toggleLocale}>
          {t('common.changeLanguage')}
        </Button>
      </main>
    </>
  );
}

/** Tester create-or-sign-in step (email + password). Rendered only when the
 * runtime `auth.email_login` app_setting is on — a pilot/tester-only auth path
 * while phone/SMS verification isn't live yet (VITE_DEV_EMAIL_AUTH's real job
 * is only setting the flag's DB default at seed time). */
function EmailStep(): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const emailAuth = useEmailAuth();

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('auth.emailInvalid'));
      return;
    }
    if (password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    emailAuth.mutate({ email, password }, { onError: (e) => setError(t(errorMessageKey(e))) });
  }

  return (
    <form className="stack" onSubmit={handleSubmit} noValidate>
      <p className="badge" style={{ color: 'var(--color-accent-text)' }}>
        {t('auth.testAccountBadge')}
      </p>
      <p className="muted">{t('auth.testAccountNote')}</p>
      <TextField
        label={t('auth.emailLabel')}
        placeholder={t('auth.emailHint')}
        value={email}
        error={error ?? undefined}
        type="email"
        ltr
        autoComplete="email"
        onChange={(e) => setEmail(e.target.value)}
      />
      <TextField
        label={t('auth.passwordLabel')}
        value={password}
        type="password"
        ltr
        autoComplete="new-password"
        onChange={(e) => setPassword(e.target.value)}
      />
      <Button type="submit" block loading={emailAuth.isPending}>
        {t('auth.createOrSignIn')}
      </Button>
    </form>
  );
}

function PhoneStep({ onSent }: { onSent: (phone: string) => void }): JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const sendOtp = useSendOtp();

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    const normalized = normalizeSaudiPhone(value);
    if (!normalized) {
      setError(t('auth.phoneInvalid'));
      return;
    }
    sendOtp.mutate(normalized, {
      onSuccess: () => onSent(normalized),
      onError: (e) => setError(t(errorMessageKey(e))),
    });
  }

  return (
    <form className="stack" onSubmit={handleSubmit} noValidate>
      <TextField
        label={t('auth.phoneLabel')}
        placeholder={t('auth.phoneHint')}
        value={value}
        error={error ?? undefined}
        inputMode="tel"
        ltr
        autoComplete="tel"
        onChange={(e) => setValue(e.target.value)}
      />
      <Button type="submit" block loading={sendOtp.isPending}>
        {t('auth.sendOtp')}
      </Button>
    </form>
  );
}

function OtpStep({ phone, onBack }: { phone: string; onBack: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const verifyOtp = useVerifyOtp();

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    if (token.length !== 6) {
      setError(t('auth.otpInvalid'));
      return;
    }
    verifyOtp.mutate({ phone, token }, { onError: (e) => setError(t(errorMessageKey(e))) });
  }

  return (
    <form className="stack" onSubmit={handleSubmit} noValidate>
      <p>{t('auth.otpSentTo', { phone })}</p>
      <TextField
        label={t('auth.otpTitle')}
        value={token}
        error={error ?? undefined}
        inputMode="numeric"
        ltr
        autoComplete="one-time-code"
        maxLength={6}
        onChange={(e) => setToken(e.target.value.replace(/[^0-9]/g, ''))}
      />
      <Button type="submit" block loading={verifyOtp.isPending}>
        {t('auth.verify')}
      </Button>
      <Button variant="text" onClick={onBack}>
        {t('common.back')}
      </Button>
    </form>
  );
}
